import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { construirReciboHtml, construirReciboTexto } from '../utils/reciboHtml';

const LIMIT = 20;

const Facturas: React.FC = () => {
  const [facturas, setFacturas] = useState<any[]>([]);
  const [stats, setStats] = useState({ total: 0, pendientes: 0, pagadas: 0, porCobrar: 0 });
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFacturas, setTotalFacturas] = useState(0);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [busquedaInput, setBusquedaInput] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [voucherFactura, setVoucherFactura] = useState<any>(null);
  const [voucherLoading, setVoucherLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await api.get('/facturas', {
        params: { page, limit: LIMIT, q: busqueda || undefined, estado: filtroEstado !== 'todos' ? filtroEstado : undefined },
      });
      setFacturas(res.data.data);
      setTotalPages(res.data.totalPages);
      setTotalFacturas(res.data.total);
    } catch {}
    finally { setLoading(false); }
  };

  const cargarStats = async () => {
    try { setStats((await api.get('/facturas/stats')).data); } catch {}
  };

  useEffect(() => { cargarStats(); }, []);
  useEffect(() => { cargar(); }, [page, busqueda, filtroEstado]);

  // Debounce de busqueda: espera a que el usuario deje de escribir antes de golpear al backend
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setBusqueda(busquedaInput); }, 400);
    return () => clearTimeout(t);
  }, [busquedaInput]);

  const generarMensuales = async () => {
    const mesActual = new Date().toLocaleString('es-PE', { month: 'long', year: 'numeric' });
    const confirmado = window.confirm(`¿Generar facturas de ${mesActual.charAt(0).toUpperCase() + mesActual.slice(1)} para todos los clientes activos?`);
    if (!confirmado) return;
    setGenerando(true);
    try {
      const res = await api.post('/facturas/generar-mensuales');
      if (res.data.generadas === 0) {
        alert('Ya existen facturas para este mes');
      } else {
        alert(`✅ ${res.data.generadas} facturas generadas`);
      }
      cargar(); cargarStats();
    } catch { alert('Error al generar facturas'); }
    finally { setGenerando(false); }
  };

  const marcarPagada = async (id: string) => {
    try { await api.put(`/facturas/${id}/pagar`); cargar(); cargarStats(); } catch { alert('Error'); }
  };

  // Obtener datos de boleta desde el backend
  const cargarBoleta = async (facturaId: string) => {
    setVoucherLoading(true);
    try {
      const res = await api.get(`/facturas/${facturaId}/boleta`);
      setVoucherFactura(res.data);
    } catch (e) {
      console.error('Error cargando boleta:', e);
      alert('Error al cargar el voucher');
    } finally {
      setVoucherLoading(false);
    }
  };

  // Convertir datos de boleta al formato que espera construirReciboHtml (formato Pago)
  const getPagoDesdeBoleta = (boleta: any) => {
    const f = boleta.factura;
    const cliente = boleta.cliente;
    const empresa = boleta.empresa;
    return {
      monto: Number(f.monto),
      metodo: 'efectivo', // default, no se guarda en factura
      createdAt: f.fechaEmision || new Date().toISOString(),
      factura: {
        numero: f.numero,
        fechaVencimiento: f.fechaVencimiento,
        mes: f.mes,
        anio: f.anio,
        descripcion: f.descripcion,
      },
      cliente: {
        nombres: cliente.nombres,
        apellidos: cliente.apellidos,
        dni: cliente.dni,
        direccion: cliente.direccion,
        paquete: boleta.paquete ? { nombre: boleta.paquete.nombre } : undefined,
      },
      cobrador: boleta.cobrador ? { telefono: '' } : undefined, // cobrador no tiene telefono en boleta
      notas: undefined,
    };
  };

  const abrirVoucher = async (factura: any) => {
    if (factura.estado !== 'pagado') return;
    await cargarBoleta(factura.id);
  };

  const imprimirVoucher = () => {
    if (!voucherFactura) return;
    const pago = getPagoDesdeBoleta(voucherFactura);
    const cliente = voucherFactura.cliente;
    const raw = (cliente.telefono || cliente.telefono2 || '').replace(/\D/g, '');
    const numero = raw.length === 9 ? '51' + raw : (raw || null);
    const waUrl = numero
      ? `https://wa.me/${numero}?text=${encodeURIComponent(construirReciboTexto(pago))}`
      : undefined;
    if (!numero) alert('El cliente no tiene teléfono registrado: se imprimirá el voucher sin el botón de WhatsApp.');
    const win = window.open('', '_blank', 'width=440,height=680');
    if (!win) return;
    win.document.write(construirReciboHtml(pago, waUrl));
    win.document.close();
  };

  const compartirVoucherWhatsApp = () => {
    if (!voucherFactura) return;
    const pago = getPagoDesdeBoleta(voucherFactura);
    const cliente = voucherFactura.cliente;
    const raw = (cliente.telefono || cliente.telefono2 || '').replace(/\D/g, '');
    const numero = raw.length === 9 ? '51' + raw : (raw || null);
    if (!numero) { alert('El cliente no tiene teléfono registrado'); return; }
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(construirReciboTexto(pago))}`, '_blank');
  };

  const cerrarVoucher = () => setVoucherFactura(null);

  const colorEstado = (e: string) => e === 'pagado' ? '#22c55e' : e === 'vencido' ? '#ef4444' : '#f59e0b';

  const meses = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h2 style={{ color:'#f1f5f9', margin:0, fontSize:24, fontWeight:800 }}>🧾 Facturas</h2>
          <p style={{ color:'#64748b', margin:'4px 0 0', fontSize:13 }}>Gestión de facturas y cobros</p>
        </div>
        <button onClick={generarMensuales} disabled={generando}
          style={{ background:'#0ea5e9', border:'none', color:'white', padding:'10px 20px', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:14, opacity: generando ? 0.7 : 1 }}>
          {generando ? '⏳ Generando...' : '⚡ Generar Facturas del Mes'}
        </button>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Total', value: stats.total, color:'#38bdf8' },
          { label:'Pendientes', value: stats.pendientes, color:'#f59e0b' },
          { label:'Pagadas', value: stats.pagadas, color:'#22c55e' },
          { label:'Por cobrar', value: `S/. ${stats.porCobrar.toFixed(2)}`, color:'#f59e0b' },
        ].map(s => (
          <div key={s.label} style={{ background:'#1e293b', borderRadius:10, padding:'14px 18px', border:`1px solid ${s.color}33` }}>
            <div style={{ color:'#64748b', fontSize:12 }}>{s.label}</div>
            <div style={{ color:s.color, fontSize:22, fontWeight:800 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display:'flex', gap:12, marginBottom:16 }}>
        <input value={busquedaInput} onChange={e => setBusquedaInput(e.target.value)}
          placeholder="🔍 Buscar cliente o número..."
          style={{ flex:1, background:'#1e293b', border:'1px solid #334155', borderRadius:8, padding:'9px 14px', color:'white', fontSize:13 }} />
        <select value={filtroEstado} onChange={e => { setPage(1); setFiltroEstado(e.target.value); }}
          style={{ background:'#1e293b', border:'1px solid #334155', borderRadius:8, padding:'9px 14px', color:'white', fontSize:13 }}>
          <option value="todos">Todos</option>
          <option value="pendiente">Pendiente</option>
          <option value="pagado">Pagado</option>
          <option value="vencido">Vencido</option>
        </select>
      </div>

      {loading ? <div style={{ color:'#64748b', textAlign:'center', padding:48 }}>Cargando...</div> :
        <div style={{ background:'#1e293b', borderRadius:12, overflow:'hidden', border:'1px solid #334155' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#0f172a' }}>
                {['N° Factura','Cliente','Período','Monto','Estado','Vencimiento','Acciones'].map(h => (
                  <th key={h} style={{ padding:'12px 16px', color:'#64748b', fontSize:12, fontWeight:600, textAlign:'left', borderBottom:'1px solid #334155' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {facturas.map((f, i) => (
                <tr key={f.id} style={{ borderBottom:'1px solid #0f172a', background: i % 2 === 0 ? 'transparent' : '#0f172a11' }}>
                  <td style={{ padding:'12px 16px', color:'#38bdf8', fontSize:13, fontWeight:600 }}>{f.numero}</td>
                  <td style={{ padding:'12px 16px', color:'#f1f5f9', fontSize:13 }}>
                    {f.cliente?.nombres} {f.cliente?.apellidos}
                    {f.esProporcional && <span style={{ marginLeft:6, background:'#8b5cf622', color:'#a78bfa', border:'1px solid #8b5cf644', borderRadius:10, padding:'2px 6px', fontSize:10 }}>Proporcional</span>}
                  </td>
                  <td style={{ padding:'12px 16px', color:'#94a3b8', fontSize:13 }}>{meses[f.mes]} {f.anio}</td>
                  <td style={{ padding:'12px 16px', color:'#22c55e', fontSize:14, fontWeight:700 }}>S/. {Number(f.monto).toFixed(2)}</td>
                  <td style={{ padding:'12px 16px' }}>
                    <span style={{ background:`${colorEstado(f.estado)}22`, color:colorEstado(f.estado), border:`1px solid ${colorEstado(f.estado)}44`, borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:700 }}>
                      {f.estado}
                    </span>
                  </td>
                  <td style={{ padding:'12px 16px', color:'#94a3b8', fontSize:13 }}>
                    {f.fechaVencimiento ? new Date(f.fechaVencimiento).toLocaleDateString('es-PE') : '—'}
                  </td>
                  <td style={{ padding:'12px 16px' }}>
                    {f.estado === 'pagado' && (
                      <button
                        onClick={() => abrirVoucher(f)}
                        disabled={voucherLoading}
                        title="Ver voucher PDF"
                        style={{
                          background: '#38bdf8',
                          border: 'none',
                          color: '#0f172a',
                          padding: '6px 12px',
                          borderRadius: 8,
                          cursor: voucherLoading ? 'not-allowed' : 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          opacity: voucherLoading ? 0.6 : 1,
                        }}
                      >
                        {voucherLoading ? '⏳' : '📄 Ver Voucher'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {facturas.length === 0 && <div style={{ textAlign:'center', color:'#64748b', padding:32 }}>No hay facturas</div>}
        </div>
      }

      {!loading && totalFacturas > 0 && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16 }}>
          <span style={{ color:'#64748b', fontSize:12 }}>
            Mostrando {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, totalFacturas)} de {totalFacturas} facturas
          </span>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              style={{ background:'#1e293b', border:'1px solid #334155', color: page <= 1 ? '#475569' : '#94a3b8', borderRadius:8, padding:'6px 14px', cursor: page <= 1 ? 'default' : 'pointer', fontSize:12, fontWeight:600 }}>
              ← Anterior
            </button>
            <span style={{ color:'#94a3b8', fontSize:12 }}>Página {page} de {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              style={{ background:'#1e293b', border:'1px solid #334155', color: page >= totalPages ? '#475569' : '#94a3b8', borderRadius:8, padding:'6px 14px', cursor: page >= totalPages ? 'default' : 'pointer', fontSize:12, fontWeight:600 }}>
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* Modal Voucher */}
      {voucherFactura && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={cerrarVoucher}
        >
          <div
            style={{
              background: '#1e293b',
              borderRadius: 12,
              padding: 24,
              maxWidth: 500,
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              border: '1px solid #334155',
              boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: '#f1f5f9', margin: 0, fontSize: 18 }}>📄 Voucher de Pago</h3>
              <button
                onClick={cerrarVoucher}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  fontSize: 24,
                  cursor: 'pointer',
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>
                <strong style={{ color: '#f1f5f9' }}>{voucherFactura.factura.numero}</strong> —{' '}
                {voucherFactura.cliente.nombres} {voucherFactura.cliente.apellidos}
              </p>
              <p style={{ color: '#64748b', fontSize: 12, margin: '4px 0 0' }}>
                Período: {meses[voucherFactura.factura.mes]} {voucherFactura.factura.anio} —{' '}
                Monto: <strong style={{ color: '#22c55e' }}>S/. {Number(voucherFactura.factura.monto).toFixed(2)}</strong>
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={imprimirVoucher}
                style={{
                  flex: 1,
                  background: '#0ea5e9',
                  border: 'none',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                🖨️ Ver / Imprimir Voucher
              </button>
              <button
                onClick={compartirVoucherWhatsApp}
                style={{
                  flex: 1,
                  background: '#25D366',
                  border: 'none',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                📱 Enviar por WhatsApp
              </button>
            </div>

            <p style={{ color: '#64748b', fontSize: 11, marginTop: 12, textAlign: 'center' }}>
              Al imprimir, use "Guardar como PDF" para descargar el voucher.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Facturas;
